import React, { useEffect, useState, useRef } from "react";
import { motion, useInView, animate } from "framer-motion";
import { TbBolt, TbEye } from "react-icons/tb";
import Card from "../../components/Card/Card";
import FeatureIcon from "../../components/FeatureIcon/FeatureIcon";
import CtaBanner from "../../components/CtaBanner/CtaBanner";
import LazyImage from "../../components/LazyImage/LazyImage";
import styles from "./OurStory.module.css";
import Badge from "../../components/Badge/Badge";
import { ifillip_company_image2 as GALLERY } from "../../controller/Image";

/* ============================================================
   COUNT-UP NUMBER   same concept used across Platform & AI Features
============================================================ */
const CountUpNumber = ({
  target,
  duration = 1.6,
  decimals = 0,
  prefix = "",
  suffix = "",
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, target, {
      duration,
      ease: "easeOut",
      onUpdate: (latest) => {
        setValue(
          decimals > 0
            ? parseFloat(latest.toFixed(decimals))
            : Math.round(latest),
        );
      },
    });
    return () => controls.stop();
  }, [isInView, target, duration, decimals]);

  const display = decimals > 0 ? value.toFixed(decimals) : value;
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

const TEAM = [
  {
    name: "Venkatraman S",
    role: "Founder & CEO",
    bio: "Venkatraman drives the overall vision of iFillip, focusing on building impactful solutions that bridge technology and education. He ensures every initiative aligns with long-term growth and real-world value.",
    img: "https://images.unsplash.com/photo-1556157382-97eda2d62296?w=600&q=80",
  },
  {
    name: "Ravisankar K",
    role: "Product Head",
    bio: "Ravisankar leads product strategy and execution, ensuring that every feature is meaningful, scalable, and aligned with user needs. He works closely with teams to deliver consistent and high-quality product experiences.",
    img: "https://images.unsplash.com/photo-1603415526960-f7e0328c63b1?w=600&q=80",
  },
  {
    name: "Tamilarasu R",
    role: "Product Design Lead",
    bio: "Tamilarasu focuses on crafting intuitive and user-friendly designs. He ensures that every interaction within iFillip is simple, effective, and aligned with modern design standards.",
    img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80",
  },
  {
    name: "Pakeerathan P",
    role: "Team Lead",
    bio: "Pakeerathan leads the development team, ensuring smooth execution of projects and maintaining high coding standards. He focuses on collaboration, performance, and delivering reliable solutions.",
    img: "https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=600&q=80",
  },
];
// const GALLERY = [
// ];

const OurStory = () => {
  return (
    <div className={styles.page}>
      {/* ============ HERO ============ */}
      <section className={`page-container ${styles.heroSection}`}>
        <div className="row align-items-center g-5">
          <div className="col-12 col-lg-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* <span className={styles.eyebrow}>ABOUT US</span> */}
              <Badge variant="cyan">ABOUT US</Badge>
              <h1 className={styles.heroTitle}>
                Our <span className="gradientText">Story</span>
              </h1>
              <p className="normalText">
                Bharat Clouds was founded with a vision to build technology
                solutions that solve real-world challenges through innovation,
                scalability, and intelligent digital experiences. Over the
                years, we have worked across modern web technologies, cloud
                infrastructure, automation, and enterprise platforms, helping
                organizations simplify operations and accelerate digital
                transformation. As education rapidly evolved toward connected
                and data-driven experiences, we recognized the need for a
                smarter and more intelligent school ecosystem. This vision led
                to the creation of iFillip — an AI-powered platform designed to
                transform the way educational institutions operate, collaborate,
                and grow.
              </p>
            </motion.div>
          </div>
          <div className="col-12 col-lg-6 text-center">
            <motion.div
              className={styles.heroVisual}
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <img
                src="https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=800&q=80"
                alt="iFillip intelligence visualization"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ WHY WE BUILT iFILLIP ============ */}
      <section className="full-width-white-section">
        <div className={`page-container ${styles.eraSection}`}>
          <div className="row align-items-center g-5">
            <div className="col-12 col-lg-6">
              <div className={styles.galleryGrid}>
                <LazyImage
                  {...GALLERY}
                  alt=""
                  wrapperClassName={styles.galleryImg}
                />
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, x: 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="subTitle">Why We Built iFillip</h2>
                <p className="normalText">
                  Traditional school management systems were designed primarily
                  for administration, often lacking the intelligence,
                  flexibility, and connected experiences required by modern
                  educational institutions. We built iFillip to bridge this gap
                  by combining AI, automation, analytics, and collaboration into
                  one unified ecosystem. Our goal is to empower schools with
                  intelligent tools that simplify operations, improve
                  communication, and support better learning outcomes for every
                  stakeholder.
                </p>
                {/* <p className={styles.eraText}>
                iFillip was built to change that. We set out to create a school
                management platform that feels calm, intelligent, and genuinely
                useful one that treats data as a tool for better teaching, not
                an end in itself.
              </p> */}
                {/* <blockquote className={styles.blockquote}>
                "The best school software should disappear into the background
                and let teachers teach."
              </blockquote> */}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ THE PEOPLE BEHIND iFILLIP (TEAM) ============ */}
      {/* <section className={`page-container ${styles.teamSection}`}>
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={styles.teamHeader}
        >
          <h2 className={styles.teamTitle}>The People Behind iFillip</h2>
          <p className={styles.teamSubtitle}>
            A team of educators, engineers, and designers who've spent years
            inside classrooms and boardrooms and decided to build something
            better.
          </p>
        </motion.header>

        <div className="row g-4">
          {TEAM.map((m, i) => (
            <div key={m.name} className="col-12 col-md-3">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <article className={styles.teamCard}>
                  <img src={m.img} alt={m.name} className={styles.teamImg} />
                  <h3 className={styles.teamName}>{m.name}</h3>
                  <div className={styles.teamRole}>{m.role}</div>
                  <p className={styles.teamBio}>{m.bio}</p>
                </article>
              </motion.div>
            </div>
          ))}
        </div>
      </section> */}

      {/* ============ MISSION ============ */}
      <section className="page-container missionSection">
        <Card variant="default" padding="lg" className="missionCard">
          <div className="row align-items-center g-5">
            <div className="col-12 col-lg-7">
              <h2 className="missionTitle">Our Mission</h2>

              <p className="normalText">
                To put educators back at the center of education supported by
                intelligent tools that simplify operations, surface meaningful
                insight, and respect the people they're built for.
              </p>

              <div className="row g-3 mt-2">
                <div className="col-12 col-sm-6">
                  <div className="missionFeature">
                    <FeatureIcon icon={TbBolt} tint="purple" size="md" />
                    <div>
                      <h4 className="subTitle">Unlock Potential</h4>
                      <p className="normalText">
                        Help teachers spot hidden strengths and gaps in every
                        student, early enough to make a difference.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6">
                  <div className="missionFeature">
                    <FeatureIcon icon={TbEye} tint="cyan" size="md" />
                    <div>
                      <h4 className="subTitle">Protect Focus</h4>
                      <p className="normalText">
                        Remove the administrative noise that pulls educators
                        away from the classroom.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-5 text-center">
              <div className="missionCircle">
                <div className="missionCirclePct">
                  <CountUpNumber target={100} suffix="%" />
                </div>
                <div className="missionCircleLabel">COMMITMENT TO</div>
                <div className="missionCircleLabel">RESPONSIBLE AI</div>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* ============ CTA ============ */}
      <section className={`page-container ${styles.ctaWrap}`}>
        <CtaBanner
          title="Ready to see what a better school platform feels like?"
          primaryLabel="Explore the Platform"
          primaryHref="/core-features"
          secondaryLabel="Talk to Our Team"
          secondaryHref="/contact"
          variant="deep-blue"
        />
      </section>
    </div>
  );
};

export default OurStory;